import { Component, Input, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  FormsModule,
} from '@angular/forms';
import {
  getGalleryYoutubeUrl,
  isVideoFileUrl,
  validateVideoFile,
  MAX_VIDEO_BYTES,
} from '@millionshow/common';
import { GalleryVideoPlayer } from './gallery-video-player.component';
import { Select } from '../select/select.component';
import { FileUploader } from '../file-uploader/file-uploader.component';
import { UploadedFile } from '../file-uploader/file-uploader.adapter';

@Component({
  selector: 'app-gallery-video-input',
  standalone: true,
  imports: [GalleryVideoPlayer, FormsModule, Select, FileUploader],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GalleryVideoInput),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => GalleryVideoInput),
      multi: true,
    },
  ],
  template: `
    <div class="flex flex-col gap-3">
      <div
        class="flex flex-col gap-2"
        [attr.inert]="disabled || uploading ? '' : null"
        [class.opacity-50]="disabled || uploading">
        <span>등록 방식</span>
        <app-select
          title="등록 방식"
          [options]="modeOptions"
          [ngModel]="mode"
          (ngModelChange)="setMode($event)"
          [ngModelOptions]="{ standalone: true }"
          [disabled]="disabled || uploading" />
      </div>
      @if (mode === 'youtube') {
      <input
        aria-label="유튜브 링크"
        class="input w-full"
        [value]="value"
        [disabled]="disabled"
        placeholder="유튜브 링크를 입력해주세요."
        (input)="updateValue($any($event.target).value)"
        (blur)="onTouched()" />
      } @else {
      <div
        [attr.inert]="disabled || uploading ? '' : null"
        [class.opacity-50]="disabled">
        <app-file-uploader
          [ngModel]="fileUrls"
          (ngModelChange)="updateValue($event?.[0] ?? '')"
          [ngModelOptions]="{ standalone: true }"
          [disabled]="disabled || uploading"
          [uploadHandler]="uploadForUploader"
          [deleteHandler]="detachFile"
          [maxlength]="1"
          [maxSize]="maxVideoBytes"
          accept=".mp4,.webm,video/mp4,video/webm"
          placeholder="MP4·WebM 동영상 파일 (최대 1개)" />
      </div>
      <p class="text-sm text-red-500 mt-1 font-medium">
        * 50MB 이하의 동영상 파일만 업로드 가능합니다.<br />
        (최대 용량 초과 시 프로그램을 사용해 비디오를 50MB 이하로 압축한 후
        업로드해주세요)
      </p>
      <p class="text-sm text-gray-500">
        지원 형식: MP4·WebM (MP4 H.264/AAC 권장)
      </p>
      } @if (uploading) {
      <p role="status">동영상 업로드 중입니다. 완료될 때까지 기다려주세요.</p>
      } @if (error) {
      <p role="alert" class="text-sm text-red-500">{{ error }}</p>
      } @if (value && !validate()) {
      <app-gallery-video-player
        [src]="value"
        class="block aspect-video w-full max-w-4xl" />
      }
    </div>
  `,
})
export class GalleryVideoInput implements ControlValueAccessor, Validator {
  mode: 'youtube' | 'file' = 'youtube';
  readonly modeOptions = [
    { label: '유튜브 링크', value: 'youtube' },
    { label: '파일 업로드', value: 'file' },
  ];
  readonly maxVideoBytes = MAX_VIDEO_BYTES;
  fileUrls: string[] = [];
  value = '';
  uploading = false;
  error = '';
  disabled = false;
  @Input() uploadHandler?: (file: File) => Promise<{ url: string }>;
  private onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};
  // Removing a selection must not delete the persisted gallery's source file.
  detachFile = async (_url: string): Promise<void> => {};
  uploadForUploader = async (file: File): Promise<UploadedFile> => {
    if (this.disabled || this.uploading)
      throw new Error('업로드할 수 없습니다.');
    await this.upload(file);
    if (this.error) throw new Error(this.error);
    return { url: this.value, filename: file.name, size: file.size };
  };
  writeValue(value: string | null): void {
    this.value = value ?? '';
    this.mode = isVideoFileUrl(this.value) ? 'file' : 'youtube';
    this.fileUrls = this.mode === 'file' ? [this.value] : [];
  }
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }
  setMode(mode: 'youtube' | 'file'): void {
    if (mode !== 'youtube' && mode !== 'file') return;
    if (this.disabled || this.uploading || this.mode === mode) return;
    this.mode = mode;
    this.updateValue('');
  }
  updateValue(value: string): void {
    this.value = value.trim();
    this.fileUrls = this.mode === 'file' && this.value ? [this.value] : [];
    this.error =
      this.mode === 'youtube' && this.value && !getGalleryYoutubeUrl(this.value)
        ? '올바른 유튜브 링크를 입력해주세요.'
        : '';
    this.onChange(this.value);
    this.onTouched();
    this.onValidatorChange();
  }
  validate(): ValidationErrors | null {
    if (this.uploading) return { uploading: true };
    const valid =
      this.mode === 'file'
        ? isVideoFileUrl(this.value)
        : !!getGalleryYoutubeUrl(this.value);
    return valid ? null : { videoRequired: true };
  }
  async upload(file: File): Promise<void> {
    if (this.disabled || this.uploading) return;
    this.error = validateVideoFile(file) ?? '';
    if (this.error) return;
    this.uploading = true;
    this.onTouched();
    this.onValidatorChange();
    try {
      if (!this.uploadHandler) throw new Error('업로드를 사용할 수 없습니다.');
      const result = await this.uploadHandler(file);
      if (!isVideoFileUrl(result.url))
        throw new Error('올바른 동영상 URL이 아닙니다.');
      this.updateValue(result.url);
    } catch {
      this.error =
        '동영상 업로드에 실패했습니다. 파일과 네트워크를 확인하고 다시 시도해주세요.';
    } finally {
      this.uploading = false;
      this.onValidatorChange();
    }
  }
}
